import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const flag = (cc) => (cc ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))) : "");

export const CityAutocomplete = ({ value, onChange, onSelect, onClear, placeholder, testid = "city-autocomplete", required, className = "" }) => {
  const { t, i18n } = useTranslation();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const skip = useRef(false);

  useEffect(() => {
    if (skip.current) { skip.current = false; return; }
    if (!value || value.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/cities", { params: { q: value.trim(), lang: i18n.resolvedLanguage || "en" } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [value, i18n.resolvedLanguage]);

  const pick = (r) => {
    skip.current = true;
    setOpen(false);
    onSelect(r);
  };

  return (
    <div className={`relative ${className}`}>
      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <Input
        data-testid={testid}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || t("global.city_placeholder")}
        className="pl-9 pr-9 h-11 bg-[#131A26] border-white/10"
        autoComplete="off"
      />
      {loading ? (
        <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-500" />
      ) : value && onClear ? (
        <button type="button" onClick={onClear} data-testid={`${testid}-clear`} aria-label={t("global.clear")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
          <X size={15} />
        </button>
      ) : null}
      {open && (
        <div data-testid={`${testid}-results`} className="absolute z-50 mt-1 w-full rounded-xl bg-[#131A26] border border-white/10 shadow-2xl overflow-hidden">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">{t("global.no_results")}</div>
          ) : (
            results.map((r, i) => (
              <button
                type="button"
                key={`${r.city}-${r.country_code}-${i}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r)}
                data-testid={`${testid}-option-${i}`}
                className="w-full text-left px-4 py-2.5 hover:bg-white/5 flex items-center gap-2 text-sm transition-colors"
              >
                <span>{flag(r.country_code)}</span>
                <span className="font-semibold">{r.city}</span>
                <span className="text-slate-500 truncate">{[r.region, r.country].filter(Boolean).join(", ")}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
